import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Plus, Trash2, Save, Copy, Music2, Sliders, Keyboard as KeyboardIcon } from 'lucide-react';
import { 
  SCALES, generateEDO, generateEDX, generateRatios, generateKeyboard, 
  genId, parseRatioToCents, roundCents, frequencyForNote, degreesPerPeriod 
} from '@/lib/microtonal/scale';
import type { Keyboard, KeyboardKey, Scale, ScaleStep } from '@/lib/microtonal/types';
import { PlayableKeyboard } from './PlayableKeyboard';
import { useMicrotonalLibrary } from '@/hooks/useMicrotonalLibrary';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

export function KeyboardEditor() {
  const { activeKeyboard, saveKeyboard, allScales } = useMicrotonalLibrary();
  const { toast } = useToast();
  
  const [editingKeyboard, setEditingKeyboard] = useState<Keyboard>(activeKeyboard);
  const [edoN, setEdoN] = useState(12);
  const [edxN, setEdxN] = useState(13);
  const [edxPeriod, setEdxPeriod] = useState('1901.955');
  const [edxName, setEdxName] = useState('Custom EDX');
  const [ratioInput, setRatioInput] = useState('9/8 5/4 4/3 3/2 5/3 15/8 2/1');
  const [ratioName, setRatioName] = useState('Custom Ratios');
  const [numPeriods, setNumPeriods] = useState(3);

  // Sync editing keyboard when active keyboard changes
  const syncFromActive = useCallback(() => {
    setEditingKeyboard(activeKeyboard);
  }, [activeKeyboard]);

  const updateScale = useCallback((newScale: Scale) => {
    const newKb = { ...editingKeyboard, scale: newScale };
    setEditingKeyboard(newKb);
  }, [editingKeyboard]);

  const updateStep = useCallback((index: number, field: keyof ScaleStep, value: string) => {
    const steps = [...editingKeyboard.scale.steps];
    if (field === 'cents') {
      const cents = parseFloat(value);
      if (!Number.isNaN(cents)) {
        steps[index] = { ...steps[index], cents: roundCents(cents) };
      }
    } else if (field === 'label') {
      steps[index] = { ...steps[index], label: value };
    } else if (field === 'ratio') {
      const cents = parseRatioToCents(value);
      if (cents !== null) {
        steps[index] = { ...steps[index], cents: roundCents(cents), ratio: value, label: value };
      }
    }
    // Sort steps by cents
    steps.sort((a, b) => a.cents - b.cents);
    updateScale({ ...editingKeyboard.scale, steps });
  }, [editingKeyboard, updateScale]);

  const addStep = useCallback(() => {
    const steps = [...editingKeyboard.scale.steps];
    const lastCents = steps.length > 0 ? steps[steps.length - 1].cents : 0;
    steps.push({ cents: roundCents(lastCents + 100), label: '' });
    updateScale({ ...editingKeyboard.scale, steps });
  }, [editingKeyboard, updateScale]);

  const removeStep = useCallback((index: number) => {
    const steps = editingKeyboard.scale.steps.filter((_, i) => i !== index);
    updateScale({ ...editingKeyboard.scale, steps });
  }, [editingKeyboard, updateScale]);

  const applyEDO = useCallback(() => {
    const scale = generateEDO(edoN, editingKeyboard.scale.baseFrequency);
    updateScale({ ...scale, id: editingKeyboard.scale.id, name: scale.name });
  }, [edoN, editingKeyboard, updateScale]);

  const applyEDX = useCallback(() => {
    const periodCents = parseFloat(edxPeriod);
    if (Number.isNaN(periodCents) || periodCents <= 0) {
      toast({ title: 'Invalid period', description: 'Period must be a positive number (in cents)' });
      return;
    }
    const scale = generateEDX(edxN, periodCents, edxName, `Custom equal division`, editingKeyboard.scale.baseFrequency);
    updateScale({ ...scale, id: editingKeyboard.scale.id, name: edxName });
  }, [edxN, edxPeriod, edxName, editingKeyboard, updateScale, toast]);

  const applyRatios = useCallback(() => {
    const ratios = ratioInput.trim().split(/\s+/).filter(Boolean);
    const scale = generateRatios(ratios, ratioName, 'Custom ratio scale', editingKeyboard.scale.baseFrequency);
    if (!scale) {
      toast({ title: 'Invalid ratios', description: 'Use format like "3/2" or "700" separated by spaces. Last value is the period.' });
      return;
    }
    updateScale({ ...scale, id: editingKeyboard.scale.id, name: ratioName });
  }, [ratioInput, ratioName, editingKeyboard, updateScale, toast]);

  const regenerateKeys = useCallback(() => {
    const newKb = generateKeyboard(editingKeyboard.scale, { numPeriods, name: editingKeyboard.name });
    setEditingKeyboard({ ...newKb, id: editingKeyboard.id, description: editingKeyboard.description });
  }, [editingKeyboard, numPeriods]);

  /** Update a single key's note name. */
  const updateKeyNoteName = useCallback((keyId: string, noteName: string) => {
    setEditingKeyboard((prev) => ({
      ...prev,
      keys: prev.keys.map((k) => (k.id === keyId ? { ...k, noteName } : k)),
    }));
  }, []);

  /** Toggle a key between white and black. */
  const updateKeyColor = useCallback((keyId: string, color: 'white' | 'black' | 'accent') => {
    setEditingKeyboard((prev) => ({
      ...prev,
      keys: prev.keys.map((k) => (k.id === keyId ? { ...k, color } : k)),
    }));
  }, []);

  /** Auto-generate note names for all keys based on the current scale. */
  const autoNoteNames = useCallback(() => {
    const NOTE_NAMES_12 = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const dpp = degreesPerPeriod(editingKeyboard.scale);
    const is12EDO = dpp === 12 && Math.abs(editingKeyboard.scale.periodCents - 1200) < 0.01;
    
    setEditingKeyboard((prev) => ({
      ...prev,
      keys: prev.keys.map((k) => {
        if (is12EDO) {
          const wrapped = ((k.degree % dpp) + dpp) % dpp;
          return { ...k, noteName: `${NOTE_NAMES_12[wrapped]}${4 + k.period}` };
        }
        return { ...k, noteName: `${k.period}.${k.degree}` };
      }),
    }));
  }, [editingKeyboard.scale]);

  const handleSave = useCallback(() => {
    saveKeyboard({ ...editingKeyboard, builtIn: false });
    toast({ title: 'Keyboard saved', description: `"${editingKeyboard.name}" saved to your library` });
  }, [editingKeyboard, saveKeyboard, toast]);

  const handleSaveAs = useCallback(() => {
    const newKb: Keyboard = { ...editingKeyboard, id: genId('kb'), name: `${editingKeyboard.name} (copy)`, builtIn: false };
    saveKeyboard(newKb);
    setEditingKeyboard(newKb);
    toast({ title: 'Saved as copy', description: `"${newKb.name}" saved to your library` });
  }, [editingKeyboard, saveKeyboard, toast]);

  const loadScale = useCallback((scaleId: string) => {
    const scale = allScales.find((s) => s.id === scaleId);
    if (scale) {
      updateScale({ ...scale, id: editingKeyboard.scale.id });
    }
  }, [allScales, editingKeyboard, updateScale]);

  const scale = editingKeyboard.scale;
  const dpp = degreesPerPeriod(scale);

  return (
    <div className="space-y-6">
      {/* Keyboard metadata */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Music2 className="size-5" /> Keyboard & Scale Editor</CardTitle>
          <CardDescription>Edit the scale (tuning system) and keyboard layout. Changes preview instantly.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="kb-name">Keyboard Name</Label>
              <Input id="kb-name" value={editingKeyboard.name} onChange={(e) => setEditingKeyboard({ ...editingKeyboard, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="kb-desc">Description</Label>
              <Input id="kb-desc" value={editingKeyboard.description ?? ''} onChange={(e) => setEditingKeyboard({ ...editingKeyboard, description: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scale-select">Load Preset Scale</Label>
              <Select onValueChange={loadScale}>
                <SelectTrigger id="scale-select"><SelectValue placeholder="Choose a scale…" /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Built-in Scales</SelectLabel>
                    {SCALES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="base-freq">Base Frequency (Hz)</Label>
              <Input id="base-freq" type="number" step="0.1" value={scale.baseFrequency} onChange={(e) => updateScale({ ...scale, baseFrequency: parseFloat(e.target.value) || 261.6256 })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="period">Period (cents)</Label>
              <Input id="period" type="number" step="0.001" value={scale.periodCents} onChange={(e) => updateScale({ ...scale, periodCents: parseFloat(e.target.value) || 1200 })} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scale generators */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Sliders className="size-5" /> Scale Generators</CardTitle>
          <CardDescription>Quickly generate common scale types: equal divisions, arbitrary period, or just-intonation ratios.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* EDO generator */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Equal Divisions of Octave (N-EDO)</h4>
              <div className="flex gap-2">
                <Input type="number" min={2} max={200} value={edoN} onChange={(e) => setEdoN(parseInt(e.target.value) || 12)} className="w-24" />
                <Button onClick={applyEDO} size="sm">Generate</Button>
              </div>
              <p className="text-xs text-muted-foreground">12 = standard, 19/22/31/53 = common microtonal, 24 = quarter-tone</p>
            </div>

            {/* EDX generator */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Equal Divisions of Arbitrary Period</h4>
              <div className="space-y-2">
                <Input placeholder="Name" value={edxName} onChange={(e) => setEdxName(e.target.value)} />
                <div className="flex gap-2">
                  <Input type="number" min={2} max={200} value={edxN} onChange={(e) => setEdxN(parseInt(e.target.value) || 13)} className="w-20" />
                  <Input type="number" step="0.001" value={edxPeriod} onChange={(e) => setEdxPeriod(e.target.value)} className="flex-1" />
                  <Button onClick={applyEDX} size="sm">Gen</Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Period 1901.955 = tritave (3/1), for Bohlen-Pierce style scales</p>
            </div>

            {/* Ratio generator */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Just Intonation (Ratios)</h4>
              <div className="space-y-2">
                <Input placeholder="Scale name" value={ratioName} onChange={(e) => setRatioName(e.target.value)} />
                <Textarea className="min-h-20 font-mono text-sm" placeholder="9/8 5/4 4/3 3/2 5/3 15/8 2/1" value={ratioInput} onChange={(e) => setRatioInput(e.target.value)} />
                <Button onClick={applyRatios} size="sm">Generate</Button>
              </div>
              <p className="text-xs text-muted-foreground">Space-separated ratios or cents. Last value = period.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Manual step editor */}
      <Card>
        <CardHeader>
          <CardTitle>Scale Steps ({dpp} degrees per period)</CardTitle>
          <CardDescription>Add, remove, or fine-tune individual scale steps. Root (0 cents) is implicit and not listed.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {scale.steps.map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs font-mono text-muted-foreground w-8">{i + 1}</span>
                <Input
                  type="number"
                  step="0.001"
                  value={step.cents}
                  onChange={(e) => updateStep(i, 'cents', e.target.value)}
                  className="w-28"
                  aria-label={`Step ${i + 1} cents`}
                />
                <span className="text-xs text-muted-foreground">c</span>
                <Input
                  placeholder="ratio (e.g. 3/2)"
                  value={step.ratio ?? ''}
                  onChange={(e) => updateStep(i, 'ratio', e.target.value)}
                  className="w-32 font-mono text-sm"
                  aria-label={`Step ${i + 1} ratio`}
                />
                <Input
                  placeholder="label"
                  value={step.label ?? ''}
                  onChange={(e) => updateStep(i, 'label', e.target.value)}
                  className="w-32"
                  aria-label={`Step ${i + 1} label`}
                />
                <span className="text-xs text-muted-foreground ml-auto">
                  {frequencyForNote(scale, i + 1, 0).toFixed(2)} Hz
                </span>
                <Button variant="ghost" size="icon" onClick={() => removeStep(i)} className="size-8">
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addStep} className="mt-2">
              <Plus className="size-4 mr-1" /> Add Step
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Keyboard layout controls */}
      <Card>
        <CardHeader>
          <CardTitle>Keyboard Layout</CardTitle>
          <CardDescription>Regenerate the keyboard key mapping from the current scale.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="num-periods">Number of Periods</Label>
              <Input id="num-periods" type="number" min={1} max={8} value={numPeriods} onChange={(e) => setNumPeriods(parseInt(e.target.value) || 3)} className="w-32" />
            </div>
            <Button onClick={regenerateKeys} size="sm">Regenerate Keys</Button>
            <div className="text-sm text-muted-foreground ml-4">
              {editingKeyboard.keys.length} keys total
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Per-key editor: note names & black/white */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyboardIcon className="size-5" /> Key Properties</CardTitle>
          <CardDescription>
            Set the note name and black/white designation for each key. Black keys are rendered shorter and overlaid on top of white keys (piano style).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <Button variant="outline" size="sm" onClick={autoNoteNames}>
              Auto-Generate Note Names
            </Button>
            <span className="text-xs text-muted-foreground">
              {editingKeyboard.keys.length} keys · {editingKeyboard.keys.filter((k) => k.color === 'white' || k.color === 'accent').length} white · {editingKeyboard.keys.filter((k) => k.color === 'black').length} black
            </span>
          </div>
          <div className="max-h-96 overflow-y-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted z-10">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Key</th>
                  <th className="text-left px-3 py-2 font-medium">Note Name</th>
                  <th className="text-left px-3 py-2 font-medium">Type</th>
                  <th className="text-left px-3 py-2 font-medium">Shortcut</th>
                  <th className="text-right px-3 py-2 font-medium">Freq</th>
                </tr>
              </thead>
              <tbody>
                {editingKeyboard.keys.map((key) => {
                  const freq = frequencyForNote(editingKeyboard.scale, key.degree, key.period);
                  return (
                    <tr key={key.id} className="border-t hover:bg-muted/50">
                      <td className="px-3 py-1.5 text-xs font-mono text-muted-foreground">
                        d{key.degree} p{key.period}
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          value={key.noteName ?? ''}
                          onChange={(e) => updateKeyNoteName(key.id, e.target.value)}
                          className="h-7 w-24 text-sm"
                          placeholder="e.g. C4"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex gap-1">
                          <Button
                            variant={key.color === 'white' || key.color === 'accent' ? 'default' : 'outline'}
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => updateKeyColor(key.id, key.degree === 0 ? 'accent' : 'white')}
                          >
                            White
                          </Button>
                          <Button
                            variant={key.color === 'black' ? 'default' : 'outline'}
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => updateKeyColor(key.id, 'black')}
                            disabled={key.degree === 0}
                          >
                            Black
                          </Button>
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="text-xs font-mono text-muted-foreground">{key.keyLabel ?? '—'}</span>
                      </td>
                      <td className="px-3 py-1.5 text-right text-xs font-mono text-muted-foreground">
                        {freq.toFixed(2)} Hz
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Live preview */}
      <Card>
        <CardHeader>
          <CardTitle>Live Preview</CardTitle>
          <CardDescription>Click keys or use your QWERTY keyboard to play. The sound is speaker-safe by design.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto pb-2">
            <PlayableKeyboard keyboard={editingKeyboard} />
          </div>
        </CardContent>
      </Card>

      {/* Save controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <Button onClick={handleSave} disabled={editingKeyboard.builtIn}>
          <Save className="size-4 mr-2" /> Save
        </Button>
        <Button variant="outline" onClick={handleSaveAs}>
          <Copy className="size-4 mr-2" /> Save As Copy
        </Button>
        {editingKeyboard.builtIn && (
          <span className="text-sm text-muted-foreground">Built-in keyboards are read-only. Use "Save As Copy" to edit.</span>
        )}
        <Separator orientation="vertical" className="h-8 mx-2" />
        <Button variant="ghost" onClick={syncFromActive}>
          Reset to Active
        </Button>
      </div>
    </div>
  );
}
